import { useCallback, useEffect, useRef, useState } from 'react';

const FADE_OUT_FALLBACK_MS = 400;
const MIN_VISIBLE_MS = 500;

type TOverlayPhase = 'hidden' | 'visible' | 'leaving';

interface IUseOverlayLifecycleReturn {
    phase: TOverlayPhase;
    isMounted: boolean;
    isLeaving: boolean;
    animateIn: boolean;
    onTransitionEnd: () => void;
}

/**
 * Ciclo de vida do overlay a partir de `isLoading`: mantém montado durante o
 * fade-out (`leaving`) antes de desmontar, respeita um tempo mínimo de exibição,
 * e só marca `animateIn` (fade-in) quando a exibição vem de um `show()` — não na
 * chegada inicial no destino, onde o overlay cobre instantâneo.
 */
const useOverlayLifecycle = (isLoading: boolean): IUseOverlayLifecycleReturn => {
    const [phase, setPhase] = useState<TOverlayPhase>(() => (isLoading ? 'visible' : 'hidden'));
    const [animateIn, setAnimateIn] = useState(false);
    const fallbackTimerRef = useRef<number | null>(null);
    const minTimerRef = useRef<number | null>(null);
    const shownAtRef = useRef<number>(0);
    const isArrivalRef = useRef<boolean>(isLoading);

    const clearFallback = useCallback(() => {
        if (fallbackTimerRef.current !== null) {
            window.clearTimeout(fallbackTimerRef.current);
            fallbackTimerRef.current = null;
        }
    }, []);

    const clearMinTimer = useCallback(() => {
        if (minTimerRef.current !== null) {
            window.clearTimeout(minTimerRef.current);
            minTimerRef.current = null;
        }
    }, []);

    const finishLeaving = useCallback(() => {
        clearFallback();
        setPhase((current) => (current === 'leaving' ? 'hidden' : current));
    }, [clearFallback]);

    const startLeaving = useCallback(() => {
        setPhase((current) => (current === 'visible' ? 'leaving' : current));
    }, []);

    useEffect(() => {
        if (isLoading) {
            clearFallback();
            clearMinTimer();
            setAnimateIn(!isArrivalRef.current);
            isArrivalRef.current = false;
            shownAtRef.current = Date.now();
            setPhase('visible');
            return undefined;
        }

        const elapsed = Date.now() - shownAtRef.current;
        const remaining = MIN_VISIBLE_MS - elapsed;
        if (remaining <= 0) {
            startLeaving();
            return undefined;
        }

        minTimerRef.current = window.setTimeout(startLeaving, remaining);
        return clearMinTimer;
    }, [isLoading, clearFallback, clearMinTimer, startLeaving]);

    useEffect(() => {
        if (phase !== 'leaving') return undefined;

        fallbackTimerRef.current = window.setTimeout(finishLeaving, FADE_OUT_FALLBACK_MS);
        return clearFallback;
    }, [phase, finishLeaving, clearFallback]);

    return {
        phase,
        isMounted: phase !== 'hidden',
        isLeaving: phase === 'leaving',
        animateIn,
        onTransitionEnd: finishLeaving
    };
};

export default useOverlayLifecycle;
